import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import "./tooltip.scss";

export const ToolTip = props => {
  return (
    <div
      className={`bounty_tooltip_content ${props.isOptions ? "bounty_tooltip_content_options" : ""}`}
    >
      {props.text}
    </div>
  );
};

export const TooltipPopper = ({
  text,
  placement = "auto",
  disabled,
  children,
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [show, setShow] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: placement,
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 10],
        },
      },
      {
        name: "popperOffsets",
        options: {
          offset: [0, 15],
        },
      },
      { name: "arrow", options: { element: arrowElement } },
    ],
  });

  return (
    <>
      <div
        className="popper-trigger"
        ref={setReferenceElement}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {createPortal(
        <div
          ref={setPopperElement}
          id="tooltip"
          role="tooltip"
          className="z-50 w-60 rounded-sm bg-[#333] p-3 text-sm text-white"
          style={styles.popper}
          data-show={disabled ? null : show}
          {...attributes.popper}
        >
          {text}
          <div
            id="arrow"
            ref={setArrowElement}
            style={styles.arrow}
            data-popper-arrow
          />
        </div>,
        document.querySelector("#root"),
      )}
    </>
  );
};

export const ToolTipToothMovement = ({
  text,
  svg,
  placement = "left",
  className = "",
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [show, setShow] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: placement,
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [-20, 10],
        },
      },
    ],
  });

  return (
    <>
      <div
        className={`tooth_movement_icon_div ${className}`}
        ref={setReferenceElement}
        onMouseEnter={() => {
          setShow(true);
        }}
        onMouseLeave={() => setShow(false)}
      >
        {svg}
      </div>
      {createPortal(
        <div
          ref={setPopperElement}
          className="bounty_tooltip_content_tooth_movement z-50"
          style={styles.popper}
          {...attributes.popper}
          data-show={show}
        >
          {text}
        </div>,
        document.querySelector("#root"),
      )}
    </>
  );
};

export const CustomTooltip = ({ text, position = "left", children }) => {
  const [visible, setVisible] = useState(false);

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);

  const dynamicOffset = useCallback(({ placement }) => {
    const commonOffset = 5;

    switch (placement) {
      case "top":
      case "bottom":
        return [0, commonOffset];
      case "left":
      case "right":
        return [commonOffset, 10];
      default:
        return [0, commonOffset];
    }
  }, []);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: position,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: dynamicOffset,
        },
      },
      {
        name: "arrow",
        options: {
          element: arrowElement,
        },
      },
      {
        name: "preventOverflow",
        options: {
          padding: 8,
        },
      },
    ],
  });

  const placement = attributes.popper?.["data-popper-placement"] || "left";

  return (
    <div className="relative inline-block">
      {/* Reference Element */}
      <div
        ref={setReferenceElement}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>

      {/* Tooltip Content */}
      {visible && (
        <div
          ref={setPopperElement}
          {...attributes.popper}
          style={{
            ...styles.popper,
            whiteSpace: "normal",
            maxWidth: "none",
            width: "max-content",
          }}
          className="relative z-50 rounded bg-gray-800 px-3 py-2 text-xs text-white shadow-md"
        >
          {text}

          {/* Tooltip Arrow */}
          {["right", "left"].includes(placement) && (
            <span
              ref={setArrowElement}
              style={styles.arrow}
              className={`absolute h-2.5 w-2.5 rotate-45 bg-gray-800 ${
                placement === "top"
                  ? "translate-center-x -bottom-1 left-1/2"
                  : placement === "bottom"
                    ? "translate-center-x -top-1 left-1/2"
                    : placement === "left"
                      ? "translate-50 -right-1 top-1/2"
                      : placement === "right"
                        ? "translate-50 -left-1 top-1/2"
                        : ""
              }`}
            />
          )}
        </div>
      )}
    </div>
  );
};
